"use client";
import Link from "next/link";
import { motion, useInView } from "framer-motion";
import { useRef, useState, useMemo } from "react";
import AsoebiModal from "@src/components/AsoebiModal";
import Dialog from "src/components/Dialog";
import { MemoCashGiftModal as CashGiftModal } from "src/components/modals/CashGiftModal";
import {
  Currency,
  GetForeignAccountDetailsQuery,
  GetForeignAccountDetailsQueryVariables,
} from "@src/@types/api.d";
import { GetForeignWalletDetails } from "@src/graphql/queries";
import toast from "react-hot-toast";
import { useQuery } from "@apollo/client";
import image from "../../../../public/images/t10/bg-icons-1.png";
import { AvailablePaymentCurrencies } from "@src/components/BirthdayTemplate1/AsoEbiLink";

interface Props {
  eventUuid: string | undefined;
  eventId: number;
  showAsoebi?: boolean;
  showCashGift?: boolean;
  isCashGiftSetup: boolean;
  availableGiftPaymentCurrencies?: AvailablePaymentCurrencies;
}

const AsoEbi = ({
  eventUuid,
  eventId,
  showAsoebi,
  showCashGift,
  isCashGiftSetup,
  availableGiftPaymentCurrencies,
}: Props) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const [openAsoebi, setOpenAsoebi] = useState(false);
  const [openCashGift, setOpenCashGift] = useState(false);

  const { data: foreignWalletDetails, loading: _foreignDetailsLoading } =
    useQuery<
      GetForeignAccountDetailsQuery,
      GetForeignAccountDetailsQueryVariables
    >(GetForeignWalletDetails, {
      skip: !eventId,
      variables: {
        eventId: !!eventId ? eventId : 0,
      },
      fetchPolicy: "network-only",
      onError(error) {
        toast.error(
          <p className="toast">{error?.message ?? "An error occurred"}</p>
        );
      },
    });

  const foreignDetails = useMemo(() => {
    const res = foreignWalletDetails?.getForeignAccountDetails
      ?.filter((item) => item?.currency !== Currency.Ngn)
      .filter(
        (item): item is NonNullable<typeof item> =>
          item !== null && item !== undefined
      );
    return res;
  }, [foreignWalletDetails]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.25, 0.25, 0.75],
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      rotateX: 15,
    },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
    hover: {
      y: -8,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
  };

  const buttonVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "backOut",
      },
    },
    hover: {
      scale: 1.05,
      boxShadow: "0 8px 25px rgba(255, 255, 255, 0.3)",
      transition: {
        duration: 0.2,
      },
    },
    tap: {
      scale: 0.95,
    },
  };

  const showBoth = showAsoebi && showCashGift;

  if (!showAsoebi && !showCashGift) {
    return null;
  }

  return (
    <motion.div
      ref={ref}
      className="bg-[#4A2C47] flex flex-col lg:flex-row items-center justify-center lg:justify-between gap-8 py-4 lg:px-10 min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <motion.div
        className={`w-[100%] lg:w-[50%] min-h-[20vh] md:min-h-screen flex flex-col justify-center items-center gap-2.5 lg:gap-6 relative`}
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        style={{
          backgroundImage: `url(${image.src})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="absolute inset-0 bg-[#4A2C47]/70 rounded-lg" />

        <motion.h3
          className="font-corGaramond text-[#FEFEFE] text-4xl lg:text-8xl italic text-center font-bold tracking-wide relative z-10"
          variants={itemVariants}
          whileHover={{
            scale: 1.05,
            textShadow: "0 0 20px rgba(255, 255, 255, 0.5)",
            transition: { duration: 0.3 },
          }}
        >
          {showBoth
            ? "WEDDING ESSENTIALS"
            : showAsoebi
            ? "OUR ASOEBI"
            : "CASH GIFT"}
        </motion.h3>
      </motion.div>

      <motion.div
        className={`text-[#FEFEFE] w-[90%] md:w-[80%] ${
          showBoth ? "lg:w-[50%]" : "lg:w-[45%]"
        } flex flex-col items-center`}
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        {showAsoebi && (
          <motion.div
            className="border-t border-[#FEFEFE] flex flex-col lg:flex-row py-12 gap-4 lg:gap-8 w-full"
            variants={cardVariants}
            whileHover="hover"
          >
            <motion.div className="w-full lg:w-[30%] flex justify-center items-center">
              <h5 className="font-dacing text-2xl lg:text-4xl font-semibold tracking-wider">
                Our Asoebi
              </h5>
            </motion.div>

            <motion.div className="w-full lg:w-[65%] flex flex-col gap-4 justify-center items-center">
              <p className="font-corGaramond text-lg lg:text-xl leading-relaxed text-center">
                Join us in celebrating our special day with beautiful
                traditional attire. Our carefully selected Asoebi represents
                unity and joy in our celebration.
              </p>

              <Dialog
                trigger={
                  <motion.button
                    className="self-start px-6 py-3 border border-[#FEFEFE] bg-transparent font-temp text-sm lg:text-base 
                             uppercase tracking-wide transition-colors duration-300 relative overflow-hidden group rounded-xl"
                    type="button"
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                  >
                    <motion.div
                      className="absolute inset-0 bg-[#FEFEFE]"
                      initial={{ x: "-100%" }}
                      whileHover={{ x: 0 }}
                      transition={{ duration: 0.3 }}
                    />
                    <span className="relative z-10 !text-[#FEFEFE] group-hover:text-[#4A2C47] transition-colors duration-300">
                      Get Your Asoebi
                    </span>
                  </motion.button>
                }
                open={openAsoebi}
                onOpenChange={setOpenAsoebi}
              >
                <AsoebiModal eventUuid={eventUuid} setOpen={setOpenAsoebi} />
              </Dialog>
            </motion.div>
          </motion.div>
        )}

        {showCashGift && (
          <motion.div
            className={`border-b border-[#FEFEFE] flex flex-col lg:flex-row py-12 gap-4 lg:gap-8 w-full`}
            variants={cardVariants}
            whileHover="hover"
          >
            <motion.div className="w-full lg:w-[30%] flex justify-center items-center">
              <h5 className="font-dacing text-2xl lg:text-4xl font-semibold tracking-wider">
                Cash Gift
              </h5>
            </motion.div>

            <motion.div className="w-full lg:w-[65%] flex flex-col gap-4 justify-center items-center">
              <p className="font-corGaramond text-lg lg:text-xl leading-relaxed text-center">
                Your presence on our special day is the most cherished gift. If
                you wish to contribute beyond your warm wishes, we gratefully
                welcome cash gifts.
              </p>

              <Dialog
                fillScreen={false}
                trigger={
                  <motion.button
                    className="self-start px-6 py-3 border border-[#FEFEFE] bg-transparent font-temp text-sm lg:text-base 
                             uppercase tracking-wide transition-colors duration-300 relative overflow-hidden group rounded-xl"
                    type="button"
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                  >
                    <motion.div
                      className="absolute inset-0 bg-[#FEFEFE]"
                      initial={{ x: "-100%" }}
                      whileHover={{ x: 0 }}
                      transition={{ duration: 0.3 }}
                    />
                    <span className="relative z-10 !text-[#FEFEFE] group-hover:text-[#4A2C47] transition-colors duration-300">
                      Give a Cash Gift
                    </span>
                  </motion.button>
                }
                open={openCashGift}
                onOpenChange={setOpenCashGift}
              >
                <CashGiftModal
                  foreignDetails={foreignDetails}
                  eventId={eventId}
                  eventUuid={eventUuid}
                  setOpen={setOpenCashGift}
                  isCashGiftSetup={isCashGiftSetup}
                  availableGiftPaymentCurrencies={
                    availableGiftPaymentCurrencies
                  }
                />
              </Dialog>
            </motion.div>
          </motion.div>
        )}

        {(showAsoebi || showCashGift) && (
          <motion.div
            className="border-b border-[#FEFEFE] w-full"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          />
        )}
      </motion.div>

      <motion.div
        className="absolute top-10 left-10 w-2 h-2 bg-[#FEFEFE]/30 rounded-full"
        animate={{
          y: [0, -15, 0],
          opacity: [0.3, 0.8, 0.3],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 4,
          repeat: Number.POSITIVE_INFINITY,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="absolute bottom-20 right-20 w-3 h-3 bg-[#FEFEFE]/20 rounded-full"
        animate={{
          y: [0, -20, 0],
          x: [0, 10, 0],
          opacity: [0.2, 0.7, 0.2],
          scale: [1, 1.3, 1],
        }}
        transition={{
          duration: 5,
          repeat: Number.POSITIVE_INFINITY,
          ease: "easeInOut",
          delay: 2,
        }}
      />

      <motion.div
        className="absolute top-1/2 left-1/4 w-1 h-1 bg-[#FEFEFE]/40 rounded-full"
        animate={{
          rotate: [0, 360],
          scale: [1, 1.5, 1],
          opacity: [0.4, 0.8, 0.4],
        }}
        transition={{
          duration: 6,
          repeat: Number.POSITIVE_INFINITY,
          ease: "linear",
          delay: 1,
        }}
      />
    </motion.div>
  );
};

export default AsoEbi;
